import mapBoxGl from 'mapbox-gl';

export const statusKeyNameMap = new Map([
  ['DRAFTED', { displayString: 'DRAFTED', statusList: ['DRAFTED'] }],
  [
    'IN_PICKUP',
    { displayString: 'IN TRANSIT (PICKUP)', statusList: ['INTRANSIT_TO_OU'] }
  ],
  ['IN_TRIP', { displayString: 'IN TRANSIT (TRIP)', statusList: ['LOADED'] }],
  [
    'AT_OU',
    {
      displayString: 'AT OU',
      statusList: ['EXCESS', 'RECEIVED_AT_OU', 'READY_FOR_DF', 'SHORTAGE']
    }
  ],
  [
    'DELIVERY',
    {
      displayString: 'DELIVERY',
      statusList: [
        'DELIVERY_PLANNED',
        'DELIVERY_LOADED',
        'OUT_FOR_DELIVERY',
        'DELIVERED_POD_PENDING'
      ]
    }
  ],
  [
    'UNDELIVERED',
    { displayString: 'UNDELIVERED', statusList: ['UNDELIVERED'] }
  ],
  ['UNEXPECTED', { displayString: 'UNEXPECTED', statusList: [] }]
]);

export const stringToRenderForCodeList = (codeList: Array<any>) => {
  return codeList.length <= 3
    ? codeList.toString()
    : `${codeList[0]} and ${codeList.length - 1} more`;
};

/**
 * this function is used to show popup data when mouse is hovered over a bubble
 * @param {*} properties
 */
export const renderPopupHtml = (properties: Object) => {
  let html = '';
  const ouCodeList = [];
  const routeCodeList = [];
  (properties.LOCATIONS || properties.locationCode)
    .split(',')
    .filter(code => code.length !== 0)
    .forEach(code => {
      if (code.indexOf('-') === -1 && ouCodeList.indexOf(code) === -1) {
        ouCodeList.push(code);
      } else if (
        code.indexOf('-') !== -1 &&
        routeCodeList.indexOf(code) === -1
      ) {
        routeCodeList.push(code);
      }
    });
  ouCodeList.sort();
  routeCodeList.sort();
  [
    { key: 'OU', codeList: ouCodeList },
    { key: 'En-route', codeList: routeCodeList }
  ].forEach(obj => {
    if (obj.codeList.length > 0) {
      html = `${html}<strong>${obj.key}</strong>: ${stringToRenderForCodeList(
        obj.codeList
      )}<br />`;
    }
  });
  html = `${html}<hr />`;
  statusKeyNameMap.forEach((v, k) => {
    if (properties[k] && properties[k] !== 0) {
      html = `${html}<strong>${v.displayString}</strong>: ${properties[k]}<br />`;
    }
  });
  return html;
};

export const mapDataSourceName = 'cnDataSource';

export const mapConfig = () =>
  new mapBoxGl.Map({
    container: 'mapDiv',
    style: 'mapbox://styles/rivigotech/ck74tc3lk0sw31ilgpu044yke',
    center: [79.08886, 23.373778],
    zoom: 3.75,
    attributionControl: false,
    maxZoom: 13.5
  })
    .addControl(
      new mapBoxGl.NavigationControl({
        showCompass: false
      })
    )
    .addControl(new mapBoxGl.FullscreenControl())
    .addControl(
      new mapBoxGl.AttributionControl({
        compact: true
      })
    )
    .addControl(new mapBoxGl.ScaleControl())
    .addControl(new mapBoxGl.GeolocateControl());

export const addMapLayers = map => {
  //   * Green, 10px circles when point count is less than 100 (also the default)
  const condition1 = [
    'any',
    ['!', ['has', 'point_count']],
    ['<', ['get', 'point_count'], 100]
  ];
  const condition2 = ['<', ['get', 'point_count'], 1000];
  const condition3 = ['>=', ['get', 'point_count'], 1000];
  //   * Yellow, 15px circles when point count is between 100 and 1000
  //   * Red, 20px circles when point count is greater than or equal to 1000

  map.addLayer({
    id: 'clusters',
    type: 'circle',
    source: mapDataSourceName,
    paint: {
      'circle-color': [
        'case',
        condition1,
        '#66ffd9',
        condition2,
        '#f1f075',
        condition3,
        '#ff6666',
        '#66ffd9'
      ],
      'circle-radius': [
        'case',
        condition1,
        10,
        condition2,
        15,
        condition3,
        20,
        10
      ]
    }
  });
  map.addLayer({
    id: 'cluster-count',
    type: 'symbol',
    source: mapDataSourceName,
    layout: {
      'text-field': ['coalesce', ['get', 'point_count_abbreviated'], '1'],
      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
      'text-size': 12,
      'text-ignore-placement': true
    }
  });

  // inspect a cluster on click
  map.on('click', 'clusters', e => {
    const features = map.queryRenderedFeatures(e.point, {
      layers: ['clusters']
    });
    const clusterId = features[0].properties.cluster_id;
    map
      .getSource(mapDataSourceName)
      .getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;

        map.easeTo({
          center: features[0].geometry.coordinates,
          zoom
        });
      });
  });

  // Create a popup, but don't add it to the map yet.
  const popup = new mapBoxGl.Popup({
    closeButton: true,
    closeOnClick: false
  });

  map.on('mouseenter', 'clusters', e => {
    // Change the cursor style as a UI indicator.
    map.getCanvas().style.cursor = 'pointer';
    const features = map.queryRenderedFeatures(e.point, {
      layers: ['clusters']
    });
    const coordinates = features[0].geometry.coordinates.slice();
    const { properties } = features[0];
    const descriptionHtml = renderPopupHtml(properties);

    // Ensure that if the map is zoomed out such that multiple
    // copies of the feature are visible, the popup appears
    // over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    // Populate the popup and set its coordinates
    popup
      .setLngLat(coordinates)
      .setHTML(descriptionHtml)
      .addTo(map);
  });

  map.on('mouseleave', 'clusters', () => {
    map.getCanvas().style.cursor = '';
    popup.remove();
  });
};

export const createData = filteredData => {
  if (!filteredData) {
    return {};
  }
  const features = filteredData.map(cn => {
    return {
      type: 'Feature',
      id: cn.cnote,
      geometry: {
        type: 'Point',
        coordinates: [cn.longitude, cn.latitude]
      },
      properties: cn
    };
  });
  return {
    type: 'FeatureCollection',
    features
  };
};

export const createSource = (map: Object, filteredData: Array<any>) => {
  const clusterProperties = {};
  statusKeyNameMap.forEach((v, k) => {
    clusterProperties[k] = ['+', ['coalesce', ['get', k], 0]];
  });
  clusterProperties.LOCATIONS = [
    ['concat', ['accumulated'], ['get', 'LOCATIONS']],
    ['concat', ',', ['get', 'locationCode']]
  ];
  map.on('load', () => {
    const data = createData(filteredData);
    map.addSource(mapDataSourceName, {
      type: 'geojson',
      data,
      cluster: true,
      clusterRadius: 20,
      clusterProperties
    });
  });
};
